(() => {
  'use strict';
  // Repeating patterns (kids-pattern products, marseladesign.com 2026-10-25, ported from the Türkiye store).
  // The customer enters the wall size (cm or in), where the wallpaper goes, one of three repeat sizes and a
  // material; the quote worker prices and freezes the selection as a one-off product (USD, or EUR for the
  // Europe market), and this script adds exactly that variant to the cart. Shared logic: assets/kind-core.js.
  // Must match PATTERNS / PATTERN_SCALES in the worker (com-tasima/desen-bordur/arac/add_kinds_com.py) and
  // snippets/pattern-registry.liquid.
  const K = globalThis.MarselaKinds;
  if (!K) return;
  const {KindError, finite, fill, MATERIALS, MAX_WIDTH_CM} = K;
  const MAX_HEIGHT_CM = 420;
  const SCALES = Object.freeze({small: Object.freeze({label: 'Small', repeatCm: 50}), medium: Object.freeze({label: 'Medium', repeatCm: 75}), large: Object.freeze({label: 'Large', repeatCm: 100})});
  // Single scale reference (owner, 2026-10-19): a crib, 135 × 90 cm.
  const REFERENCES = Object.freeze({crib: {widthCm: 135, heightCm: 90, right: 0.06}});
  const EXAMPLE_WALL = Object.freeze({widthCm: 300, heightCm: 240});
  const FORM_TEXT = Object.freeze({
    example: 'Example wall: 300 × 240 cm. Enter your own size.',
    caption: 'Your wall: {width} × {height}{where} · {scale} scale, repeats every {repeat}',
    whereLower: ' · wallpaper on the lower part, up to {split} from the floor',
    whereUpper: ' · wallpaper on the upper part, from {split} to the ceiling',
    exampleWhere: 'In the example{where}.',
    lineHintLower: 'The wallpaper goes from the floor up to this height. Most homes put the divide at 90–120 cm (35–47 in), with a chair rail.',
    lineHintUpper: 'The wallpaper goes from this height up to the ceiling. A crib is about 90 cm (35 in) tall; put the divide at 100–110 cm (39–43 in) and the pattern shows above it.'
  });

  function patternFrom(data) {
    const pattern = {handle: data?.patternHandle, reference: data?.patternReference, sha256: data?.patternSha256, asset: data?.patternAsset, sourceWidth: Number(data?.patternWidth), sourceHeight: Number(data?.patternHeight), src: data?.patternSrc};
    if (typeof pattern.handle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(pattern.handle) || !/^(?:MWC19\d{2}|ATL\d{4}-[A-Z]+)$/.test(pattern.reference || '') || !pattern.handle.endsWith(`-${pattern.reference.toLowerCase()}`)) return null;
    if (!/^[a-f0-9]{64}$/.test(pattern.sha256 || '') || pattern.asset !== `customizer-pattern-${pattern.reference.toLowerCase()}.png` || pattern.sourceWidth !== 1600 || pattern.sourceHeight !== 1600 || typeof pattern.src !== 'string' || !pattern.src) return null;
    return Object.freeze(pattern);
  }
  function canonicalConfiguration(input, pattern) {
    if (!pattern || input?.schemaVersion !== 1 || input.kind !== 'pattern' || input.productHandle !== pattern.handle || input.artwork?.reference !== pattern.reference || input.artwork?.sha256 !== pattern.sha256) throw new KindError('selection');
    const wall = input.wall;
    if (!wall || !finite(wall.widthCm) || !finite(wall.heightCm) || wall.widthCm <= 0 || wall.heightCm <= 0 || wall.widthCm > MAX_WIDTH_CM || wall.heightCm > MAX_HEIGHT_CM) throw new KindError('selection');
    if (typeof input.scale !== 'string' || !Object.hasOwn(SCALES, input.scale) || typeof input.material !== 'string' || !Object.hasOwn(MATERIALS, input.material)) throw new KindError('selection');
    return {schemaVersion: 1, kind: 'pattern', productHandle: pattern.handle, artwork: {reference: pattern.reference, sha256: pattern.sha256}, wall: {widthCm: wall.widthCm, heightCm: wall.heightCm}, scale: input.scale, material: input.material};
  }
  function validateQuote(data, configuration, pattern, endpoint, ready, currency = 'USD') {
    const c = data?.configuration;
    let returned;
    try { returned = canonicalConfiguration({schemaVersion: c?.schemaVersion, kind: c?.kind, productHandle: c?.productHandle, artwork: {reference: c?.artwork?.reference, sha256: c?.artwork?.sha256}, wall: c?.wall, scale: c?.pattern?.scale, material: c?.material?.id}, pattern); } catch (_) { throw new KindError('quote'); }
    if (JSON.stringify(returned) !== JSON.stringify(configuration)) throw new KindError('quote');
    const scale = SCALES[configuration.scale];
    if (c.pattern.label !== scale.label || c.pattern.repeatCm !== scale.repeatCm || c.pattern.origin !== 'top-left') throw new KindError('quote');
    return K.validateCommon(data, configuration, pattern, endpoint, ready, currency, 'vectorSha256');
  }
  // Half-wall placement (owner, 2026-10-19): the configuration holds only the wallpaper that is produced;
  // the wall and the divide height ride along on the cart line.
  function placementProperties(c, placement) {
    const size = `${K.plain(c.wall.widthCm)} × ${K.plain(c.wall.heightCm)} cm`;
    if (!placement) return {'Wall dimensions': size};
    const {mode, splitCm, wallHeightCm} = placement;
    const paper = mode === 'lower' ? splitCm : K.minusCm(wallHeightCm, splitCm);
    if (!['lower', 'upper'].includes(mode) || !finite(splitCm) || !finite(wallHeightCm) || splitCm <= 0 || splitCm >= wallHeightCm || wallHeightCm > MAX_HEIGHT_CM || paper !== c.wall.heightCm) throw new KindError('selection');
    return {'Wall dimensions': `${K.plain(c.wall.widthCm)} × ${K.plain(wallHeightCm)} cm`, Placement: mode === 'lower' ? `Lower part, up to ${K.plain(splitCm)} cm from the floor` : `Upper part, from ${K.plain(splitCm)} cm to the ceiling`, 'Wallpaper size': size};
  }
  function properties(quoteData, placement = null) {
    const c = quoteData.configuration;
    return K.baseProperties(quoteData, {...placementProperties(c, placement), 'Pattern scale': `${c.pattern.label} · ${c.pattern.repeatCm} cm repeat`});
  }
  // Exercised by com-tasima/desen-bordur/arac/test_kinds_contract.mjs.
  if (typeof module === 'object' && module.exports) module.exports = {SCALES, patternFrom, canonicalConfiguration, validateQuote, properties};
  if (typeof document === 'undefined') return;

  const sized = (src, width) => { try { const url = new URL(src, location.href); url.searchParams.set('width', String(width)); return url.href; } catch (_) { return src; } };

  function mount(form) {
    if (form.dataset.msPatternReady) return;
    const pattern = patternFrom(form.dataset);
    if (!pattern) return;
    form.dataset.msPatternReady = 'true';
    const $ = selector => form.querySelector(selector), $$ = selector => [...form.querySelectorAll(selector)];
    const width = $('[data-ms-pattern-width]'), height = $('[data-ms-pattern-height]'), error = $('[data-ms-pattern-error]');
    const placements = $$('[data-ms-pattern-placement]'), line = $('[data-ms-pattern-line]'), lineGroup = $('[data-ms-pattern-line-group]'), lineError = $('[data-ms-pattern-line-error]'), lineHint = $('[data-ms-pattern-line-hint]');
    const scales = $$('[data-ms-pattern-scale]'), materials = $$('[data-ms-pattern-material]');
    const stage = $('[data-ms-pattern-stage]'), wallBox = $('[data-ms-pattern-wall]'), canvas = $('[data-ms-pattern-canvas]'), caption = $('[data-ms-pattern-caption]');
    const produced = $('[data-ms-pattern-produced]'), panels = $('[data-ms-pattern-panels]'), area = $('[data-ms-pattern-area]'), total = $('[data-ms-pattern-total]'), priceLabel = $('[data-ms-pattern-price-label]');
    const button = $('[data-ms-pattern-add]'), buttonLabel = $('[data-ms-pattern-add-label]'), status = $('[data-ms-pattern-status]'), viewCart = $('[data-ms-pattern-cart]');
    const crib = $('[data-ms-pattern-ref="crib"]');
    if (!width || !height || !canvas || !wallBox || !button || !status) return;
    const page = K.pageContext(), fmt = K.formatter(page.locale), TEXT = K.textFor(form, FORM_TEXT, page.eurEnabled);
    const client = K.createClient({endpoint: page.endpoint, root: page.root});
    const enabled = page.enabled && Boolean(client.service);
    const store = K.storage(`marsela-pattern-v1:${pattern.handle}:${K.RATE_VERSION}`);
    viewCart.href = client.route('cart');
    $$('[data-ms-pattern-swatch]').forEach(node => { node.style.backgroundImage = `url("${sized(pattern.src, 400).replace(/"/g, '%22')}")`; });
    const units = K.bindUnit(form, [width, height, line].filter(Boolean), () => changed());

    // Paint for the part of the wall without wallpaper: the pattern's own average colour, lifted towards white.
    let paint = '#f1ece4';
    const image = new Image();
    image.decoding = 'async';
    image.addEventListener('load', () => {
      try {
        const probe = document.createElement('canvas'); probe.width = probe.height = 8;
        const p = probe.getContext('2d'); p.drawImage(image, 0, 0, 8, 8);
        const px = p.getImageData(0, 0, 8, 8).data; let r = 0, g = 0, b = 0;
        for (let i = 0; i < px.length; i += 4) { r += px[i]; g += px[i + 1]; b += px[i + 2]; }
        const n = px.length / 4, mix = v => Math.round(v / n * 0.32 + 255 * 0.68);
        paint = `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
      } catch (_) {}
      schedule();
    });
    image.src = sized(pattern.src, 1600);

    let phase = 'idle', errorCode = null, busy = false, slowTimer = 0, slow = false;
    const chosen = list => list.find(input => input.checked)?.value;
    // The scale names as the page shows them (translated in snippets/kind-text.liquid).
    const scaleName = key => scales.find(input => input.value === key)?.closest('label')?.querySelector('strong')?.textContent.trim() || SCALES[key]?.label || key;
    function readWall() {
      const w = units.read(width), h = units.read(height), mode = chosen(placements) || 'full';
      const invalidField = (field, value, max) => field.value.trim() !== '' && (value === null || value > max);
      const wallOk = w !== null && h !== null && w <= MAX_WIDTH_CM && h <= MAX_HEIGHT_CM;
      const split = mode === 'full' ? null : units.read(line);
      const splitTyped = mode !== 'full' && Boolean(line?.value.trim());
      const splitOk = mode === 'full' || (split !== null && (!wallOk || split < h));
      let paperHeight = null;
      if (wallOk && splitOk) paperHeight = mode === 'full' ? h : mode === 'lower' ? split : K.minusCm(h, split);
      const valid = wallOk && splitOk && paperHeight > 0;
      return {valid, mode, split, wall: wallOk ? {widthCm: w, heightCm: h} : null, paper: valid ? {widthCm: w, heightCm: paperHeight} : null, invalid: invalidField(width, w, MAX_WIDTH_CM) || invalidField(height, h, MAX_HEIGHT_CM), splitInvalid: splitTyped && !splitOk};
    }
    function currentConfiguration() {
      const state = readWall();
      if (!state.valid) return null;
      try { return canonicalConfiguration({schemaVersion: 1, kind: 'pattern', productHandle: pattern.handle, artwork: {reference: pattern.reference, sha256: pattern.sha256}, wall: state.paper, scale: chosen(scales), material: chosen(materials)}, pattern); } catch (_) { return null; }
    }
    // Placement as the customer chose it; travels with the cart line for production.
    function placementOf(state) {
      if (!state.valid || state.mode === 'full') return null;
      return {mode: state.mode, splitCm: state.split, wallHeightCm: state.wall.heightCm};
    }
    function draw() {
      const state = readWall(), scaleKey = chosen(scales) || 'medium', scale = SCALES[scaleKey] || SCALES.medium;
      const wall = state.wall || EXAMPLE_WALL;
      const mode = state.mode;
      let split = state.split;
      if (mode !== 'full' && !(split > 0 && split < wall.heightCm)) split = Math.min(100, wall.heightCm / 2);
      const maxWidth = stage.clientWidth || 320, maxHeight = Math.min(360, Math.max(180, window.innerHeight * 0.42));
      let boxWidth = maxWidth, boxHeight = maxWidth * wall.heightCm / wall.widthCm;
      if (boxHeight > maxHeight) { boxHeight = maxHeight; boxWidth = maxHeight * wall.widthCm / wall.heightCm; }
      wallBox.style.width = `${Math.max(24, boxWidth)}px`; wallBox.style.height = `${Math.max(24, boxHeight)}px`;
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(boxWidth * ratio)); canvas.height = Math.max(1, Math.round(boxHeight * ratio));
      const ctx = canvas.getContext('2d'), perCm = canvas.height / wall.heightCm;
      // Wallpaper band: the whole wall, the part below the divide, or the part above it.
      const top = mode === 'upper' ? 0 : mode === 'lower' ? canvas.height - split * perCm : 0;
      const bottom = mode === 'upper' ? canvas.height - split * perCm : canvas.height;
      ctx.fillStyle = mode === 'full' ? '#efe9df' : paint; ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (image.complete && image.naturalWidth) {
        // One repeat = repeatCm of wall; the pattern starts at the top-left corner of the wallpaper.
        const step = canvas.width * scale.repeatCm / wall.widthCm;
        const fillPattern = ctx.createPattern(image, 'repeat');
        ctx.save(); ctx.beginPath(); ctx.rect(0, top, canvas.width, bottom - top); ctx.clip();
        if (fillPattern && typeof fillPattern.setTransform === 'function' && typeof DOMMatrix === 'function') {
          fillPattern.setTransform(new DOMMatrix().translate(0, top).scale(step / image.naturalWidth, step / image.naturalHeight));
          ctx.fillStyle = fillPattern; ctx.fillRect(0, top, canvas.width, bottom - top);
        } else {
          for (let y = top; y < bottom; y += step) for (let x = 0; x < canvas.width; x += step) ctx.drawImage(image, x, y, step + 0.5, step + 0.5);
        }
        ctx.restore();
      }
      // Panel joins (at most 104 cm wide each) across the wallpaper only.
      const plan = K.panelPlan(wall);
      ctx.save(); ctx.strokeStyle = 'rgba(38,62,53,.42)'; ctx.lineWidth = ratio; ctx.setLineDash([5 * ratio, 4 * ratio]);
      for (let index = 1; index < plan.count; index++) { const x = Math.round(canvas.width * index / plan.count) + 0.5; ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, bottom); ctx.stroke(); }
      ctx.restore();
      if (mode !== 'full') {
        // A slim trim at the divide, as a rail or moulding would sit there.
        const y = canvas.height - split * perCm, band = Math.max(3 * ratio, 4 * perCm);
        ctx.fillStyle = '#fbf8f2'; ctx.fillRect(0, y - band / 2, canvas.width, band);
        ctx.fillStyle = 'rgba(38,62,53,.18)'; ctx.fillRect(0, y + band / 2, canvas.width, Math.max(1, ratio));
        ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fillRect(0, y - band / 2, canvas.width, Math.max(1, ratio));
      }
      if (crib) {
        crib.style.width = `${Math.min(100, REFERENCES.crib.widthCm / wall.widthCm * 100)}%`;
        crib.style.height = `${Math.min(100, REFERENCES.crib.heightCm / wall.heightCm * 100)}%`;
        crib.style.right = `${REFERENCES.crib.right * 100}%`;
      }
      const unit = units.unit, where = mode === 'lower' ? fill(TEXT.whereLower, {split: fmt.length(split, unit)}) : mode === 'upper' ? fill(TEXT.whereUpper, {split: fmt.length(split, unit)}) : '';
      caption.textContent = state.wall
        ? fill(TEXT.caption, {width: fmt.length(wall.widthCm, unit), height: fmt.length(wall.heightCm, unit), where, scale: scaleName(scaleKey), repeat: fmt.length(scale.repeatCm, unit)})
        : `${TEXT.example}${where ? ' ' + fill(TEXT.exampleWhere, {where}) : ''}`;
    }
    let frame = 0;
    function schedule() { if (!frame) frame = requestAnimationFrame(() => { frame = 0; draw(); }); }
    function render() {
      const state = readWall(), configuration = currentConfiguration();
      error.hidden = !state.invalid;
      if (lineGroup) lineGroup.hidden = state.mode === 'full';
      if (lineError) lineError.hidden = !state.splitInvalid;
      if (lineHint) lineHint.textContent = state.mode === 'upper' ? TEXT.lineHintUpper : TEXT.lineHintLower;
      if (configuration) {
        const plan = K.panelPlan(configuration.wall), m2 = configuration.wall.widthCm * configuration.wall.heightCm / 10000, areaText = `${fmt.number(m2, 2)} m²`;
        if (produced) produced.textContent = fmt.size(configuration.wall, units.unit);
        panels.textContent = `${plan.count} × ${fmt.length(plan.panelWidthCm, units.unit)}`;
        area.textContent = m2 < K.MIN_BILLABLE_M2 ? fill(TEXT.pricedAs, {area: areaText}) : areaText;
        total.textContent = fmt.money(K.expectedPrice(configuration, page.displayCurrency), page.displayCurrency);
      } else { if (produced) produced.textContent = '—'; panels.textContent = '—'; area.textContent = '—'; total.textContent = '—'; }
      if (priceLabel) priceLabel.textContent = phase === 'added' ? TEXT.price : TEXT.estimate;
      buttonLabel.textContent = phase === 'preparing' ? TEXT.preparing : phase === 'adding' || phase === 'added' ? TEXT.adding : phase === 'uncertain' ? TEXT.view : phase === 'failed' ? TEXT.retry : TEXT.confirmAdd;
      button.disabled = busy || !enabled;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      status.textContent = !enabled ? TEXT.unavailable : errorCode ? (TEXT[errorCode] || TEXT.network) : phase === 'preparing' ? (slow ? TEXT.slow : TEXT.preparingNote) : phase === 'adding' ? TEXT.adding : phase === 'added' ? TEXT.added : TEXT.idle;
      viewCart.hidden = !(phase === 'uncertain' || phase === 'added');
      schedule();
    }
    function changed() {
      if (busy) return;
      if (phase !== 'idle' && phase !== 'uncertain') { phase = 'idle'; errorCode = null; }
      render();
    }
    [width, height, line].filter(Boolean).forEach(field => {
      field.addEventListener('input', changed);
      field.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); field.blur(); } });
    });
    [...placements, ...scales, ...materials].forEach(input => input.addEventListener('change', changed));
    form.addEventListener('submit', event => event.preventDefault());
    canvas.addEventListener('contextmenu', event => event.preventDefault());

    button.addEventListener('click', async () => {
      if (phase === 'uncertain') { location.assign(client.route('cart')); return; }
      if (busy || !enabled) return;
      const state = readWall(), configuration = currentConfiguration(), placement = placementOf(state);
      if (!configuration) {
        errorCode = 'selection'; phase = 'failed'; render();
        (units.read(width) === null ? width : units.read(height) === null ? height : line && state.mode !== 'full' ? line : button).focus();
        return;
      }
      busy = true; phase = 'preparing'; errorCode = null; slow = false; render();
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 75000);
      slowTimer = setTimeout(() => { slow = true; render(); }, 25000);
      try {
        const current = await client.cart(controller.signal), currency = current.currency;
        if (!K.currencyAllowed(currency, page.eurEnabled)) throw new KindError('currency');
        const key = K.reuseKey(store.read(value => canonicalConfiguration(value, pattern)), configuration, currency);
        store.write({configuration, idempotencyKey: key, currency, placement, unit: units.unit});
        const ready = await client.quote(configuration, currency, key, controller.signal, (data, isReady) => validateQuote(data, configuration, pattern, client.service, isReady, currency));
        clearTimeout(slowTimer); phase = 'adding'; render();
        await client.add(ready, currency, properties(ready, placement), controller.signal);
        phase = 'added'; render();
        location.assign(client.route('cart'));
      } catch (failure) {
        const code = failure?.name === 'AbortError' || failure?.code === 'cancelled' ? (phase === 'adding' ? 'uncertain' : 'pending') : typeof failure?.code === 'string' && Object.hasOwn(TEXT, failure.code) ? failure.code : 'network';
        errorCode = code; phase = code === 'uncertain' ? 'uncertain' : 'failed';
      } finally { clearTimeout(timeout); clearTimeout(slowTimer); busy = false; slow = false; render(); }
    });

    const stored = store.read(value => canonicalConfiguration(value, pattern));
    if (stored) {
      const saved = stored.placement && ['lower', 'upper'].includes(stored.placement.mode) && stored.placement.wallHeightCm > 0 ? stored.placement : null;
      units.set(stored.unit);
      width.value = units.show(stored.configuration.wall.widthCm);
      height.value = units.show(saved ? saved.wallHeightCm : stored.configuration.wall.heightCm);
      placements.forEach(input => { input.checked = input.value === (saved ? saved.mode : 'full'); });
      if (saved && line) line.value = units.show(saved.splitCm);
      scales.forEach(input => { input.checked = input.value === stored.configuration.scale; });
      materials.forEach(input => { input.checked = input.value === stored.configuration.material; });
    }
    if (typeof ResizeObserver === 'function') new ResizeObserver(schedule).observe(stage);
    else window.addEventListener('resize', schedule);
    render();
  }

  // Cart: show each pattern line as its wallpaper, tiled at the chosen scale.
  function bindCart(scope = document) {
    scope.querySelectorAll('[data-ms-cart-pattern]').forEach(frame => {
      if (frame.dataset.patternReady) return;
      let c; try { c = JSON.parse(frame.dataset.msCartPattern); } catch (_) { return; }
      const wall = c?.wall, repeat = c?.pattern?.repeatCm;
      if (c?.kind !== 'pattern' || !wall || !(wall.widthCm > 0) || !(wall.heightCm > 0) || !Object.values(SCALES).some(scale => scale.repeatCm === repeat)) return;
      let source; try { source = new URL(c.artwork.sourceUrl); } catch (_) { return; }
      if (source.protocol !== 'https:' || source.hostname !== 'cdn.shopify.com') return;
      source.searchParams.set('width', '600');
      const tile = frame.querySelector('.ms-cart__pattern');
      if (!tile) return;
      frame.dataset.patternReady = 'true';
      const aspect = wall.widthCm / wall.heightCm;
      tile.style.aspectRatio = `${wall.widthCm} / ${wall.heightCm}`;
      tile.style.width = aspect >= 0.75 ? '100%' : 'auto';
      tile.style.height = aspect >= 0.75 ? 'auto' : '100%';
      tile.style.backgroundImage = `url("${source.href.replace(/"/g, '%22')}")`;
      tile.style.backgroundSize = `${repeat / wall.widthCm * 100}% auto`;
      frame.classList.add('is-ready');
    });
  }
  function init(scope = document) { scope.querySelectorAll('[data-ms-pattern-entry]').forEach(mount); bindCart(scope); }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
